import { AlertDialogOptions } from '../shared/alert-dialog/alert-dialog.service';

export type ConfigRequestAlertKind = 'subscription' | 'billingPlan';

export interface SubscriptionCreateAlertOptions {
  configRequestKind?: ConfigRequestAlertKind;
}

function responseMessage(body: any): string {
  if (body == null) {
    return '';
  }
  if (typeof body === 'string') {
    return body;
  }
  return String(body.message || '').trim();
}

function errorPayload(error: any): any {
  if (!error) {
    return {};
  }
  const body = error.error;
  if (body && body.data && typeof body.data === 'object') {
    return body.data;
  }
  if (body && typeof body === 'object') {
    return body;
  }
  return { message: error.message };
}

function legacyCreatedIds(resp: any): string[] | undefined {
  if (!resp) {
    return undefined;
  }
  if (Array.isArray(resp.ids) && resp.ids.length > 0) {
    return resp.ids.map(String);
  }
  if (resp.id != null && resp.id !== '') {
    return [String(resp.id)];
  }
  return undefined;
}

function configRequestViewPath(kind: ConfigRequestAlertKind, requestId: string): string {
  const base = kind === 'billingPlan' ? '/accessfee/rule-requests' : '/subscription/rule-requests';
  return `${base}/${requestId}`;
}

function isAutoApproved(resp: any): boolean | undefined {
  if (!resp) {
    return undefined;
  }
  if (typeof resp.autoApproved === 'boolean') {
    return resp.autoApproved;
  }
  if (typeof resp.autoApprove === 'boolean') {
    return resp.autoApprove;
  }
  return undefined;
}

function asIdList(value: any): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => String(item)).filter(item => item !== '');
}

function pendingRequestIds(resp: any): string[] {
  const requestIds = asIdList(resp && resp.requestIds);
  if (requestIds.length > 0) {
    return requestIds;
  }
  if (resp && resp.requestId != null && resp.requestId !== '') {
    return [String(resp.requestId)];
  }
  const ids = asIdList(resp && resp.ids);
  if (ids.length > 0) {
    return ids;
  }
  if (resp && resp.id != null && resp.id !== '') {
    return [String(resp.id)];
  }
  return [];
}

export function subscriptionOperationFailureAlert(error: any): AlertDialogOptions {
  const body = errorPayload(error);
  const message = (String(body.message || '').trim() || String(body.description || '').trim() || 'Operation failed');
  return {
    title: 'Failed',
    message
  };
}

export function subscriptionUpdateSuccessAlert(resp: any): AlertDialogOptions {
  const message = responseMessage(resp) || 'Updated successfully';
  return {
    title: 'Success',
    message
  };
}

export function subscriptionCreateSuccessAlert(
  resp: any,
  options?: SubscriptionCreateAlertOptions
): AlertDialogOptions {
  const message = responseMessage(resp) || 'created successfully';
  const kind = (options && options.configRequestKind) || 'subscription';
  const autoApproved = isAutoApproved(resp);

  if (autoApproved === true) {
    return {
      title: 'Success',
      message
    };
  }

  if (autoApproved === false) {
    const requestIds = pendingRequestIds(resp);
    if (requestIds.length > 0) {
      return {
        title: 'Success',
        message,
        links: requestIds.map(requestId => ({
          label: `View request ${requestId}`,
          url: configRequestViewPath(kind, requestId)
        }))
      };
    }
    return {
      title: 'Success',
      message
    };
  }

  const ids = legacyCreatedIds(resp);
  return {
    title: 'Success',
    message,
    value: ids
  };
}

export function subscriptionAccessFeeCreateAlert(
  resp: any,
  options?: SubscriptionCreateAlertOptions
): AlertDialogOptions {
  const rejected = Array.isArray(resp && resp.rejectedPlans)
    ? resp.rejectedPlans.filter(Boolean)
    : [];
  if (rejected.length > 0) {
    const reason = String((resp && resp.reason) || '').trim();
    const backendMessage = responseMessage(resp);
    let message = backendMessage;
    if (reason) {
      message = message ? `${message}\n\nReason: ${reason}` : `Reason: ${reason}`;
    }
    return {
      title: 'Failed',
      message: message || undefined,
      value: rejected.map(String)
    };
  }
  return subscriptionCreateSuccessAlert(resp, {
    configRequestKind: (options && options.configRequestKind) || 'billingPlan'
  });
}
